// The typed profile tree (spec/dashboard.md section 11.2): one root per collection, the keys as the sections of the pinned
// release's profile template, the fields through the section 6.4 widgets (the system profile's time fields as instants and
// durations), references read-only and resolved through the release's branch, the profile lists as named rows with the
// guided actions. Every edit stages a `player-key` draft of the environment workspace (section 11.4). A control the caller
// may not use is disabled with the permission in its title (T15); `unnySystem` is locked for everyone (section 17.1).
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Json, Parameter, Player, SaveCollection, SaveKey, Template } from '../api/types';
import { useContent } from '../content/ContentContext';
import { typeLabel } from '../content/schema';
import { joinPath } from '../content/widgets/context';
import { FieldWidget, JsonField } from '../content/widgets/FieldWidget';
import { useGame } from '../shell/GameContext';
import { routes } from '../shell/routes';
import { Button } from '../ui/Button';
import { Badge, type Tone } from '../ui/Chip';
import { NumberInput } from '../ui/inputs';
import { formatInstant, useTimeZone } from '../ui/time';
import { DateTimeInput, DurationInput } from '../ui/values';
import { useWorkspace, useWorkspaceVersion } from '../workspace/react';
import { entityKey } from '../workspace/Workspace';
import { GiveItemsDialog } from './GiveItemsDialog';
import { COOLDOWN_LIST, INSTANCE_LISTS, READ_ONLY_KEYS, collectionTemplate, obj, playerKeyId, purchaseDefaults, rowTitle, sectionDefaults, shortPlayerId, timeKind, withoutIndex } from './model';
import { ReleaseCatalogProvider, type ReleaseInfo } from './releaseCatalog';
import { useCollection } from './usePlayer';
import './tree.css';
import { ChevronRight, Database } from '../ui/icons';
import { LoadError, SkeletonRows } from '../ui/PageStates';

export interface PlayerTreeProps {
  env: string;
  player: Player;
  release: ReleaseInfo;
  canWrite: boolean;
  /** opens the History drawer for a collection or one of its keys */
  onHistory?: (collection: string, key?: string) => void;
}

/** How a node may be changed: the inputs are disabled when `readOnly`; the action buttons hide when `locked` and carry `missing` otherwise. */
interface EditMode {
  readOnly: boolean;
  /** the key is never editable (`unnySystem`): no action is offered at all */
  locked: boolean;
  /** the permission the caller lacks (`players_write`), named in every action's title */
  missing: string | null;
}

export const WRITE_PERMISSION = 'players_write';
const SOURCE_TONE: Record<string, Tone> = { client: 'neutral', admin: 'accent', restore: 'warning', reset: 'warning', import: 'info', delete: 'danger' };

export function formatBytesShort(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return String(value);
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

/** A synthetic `any` parameter: the JSON editor for untyped values. */
export function jsonParam(name: string): Parameter {
  return { id: name, unnyId: null, name, displayName: null, description: null, type: 'any', of: null, template: null, enum: null, group: null, assetKinds: [], regex: null, default: null, isUnique: false, isRequired: false, usedInName: false, flags: [], legacyFlags: 0, declaredBy: null } as unknown as Parameter;
}

export function PlayerTree(props: PlayerTreeProps) {
  return (
    <ReleaseCatalogProvider info={props.release}>
      <TreeBody {...props} />
    </ReleaseCatalogProvider>
  );
}

function TreeBody({ env, player, release, canWrite, onHistory }: PlayerTreeProps) {
  if (player.collections.length === 0) return <p className="muted" data-testid="player-tree-empty">This player has no saves yet.</p>;
  return (
    <div className="player-tree" data-testid="player-tree">
      {release.noPin && (
        <div className="banner banner-warning small" role="note">
          No release is pinned on this environment: the tree is untyped.
        </div>
      )}
      {player.collections.map((c) => (
        <CollectionNode key={c.collection} env={env} player={player} summary={c} release={release} canWrite={canWrite} onHistory={onHistory} />
      ))}
    </div>
  );
}

function CollectionNode({ env, player, summary, release, canWrite, onHistory }: { env: string; player: Player; summary: SaveCollection; release: ReleaseInfo; canWrite: boolean; onHistory?: (collection: string, key?: string) => void }) {
  const detail = useCollection(env, player.id, summary.collection);
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const root = useMemo(() => collectionTemplate(summary.collection, release.templates), [summary.collection, release.templates]);
  const keys = detail.data?.keys ?? [];
  const byName = useMemo(() => new Map(keys.map((k) => [k.key, k])), [keys]);
  const sections = root ? root.effectiveParameters.filter((p) => p.type === 'profile_data') : [];
  const named = new Set(sections.map((p) => p.name));
  const extra = keys.filter((k) => !named.has(k.key));
  return (
    <section className="ptree-collection" data-testid={`collection-${summary.collection}`} aria-label={summary.collection}>
      <div className="row ptree-collection-head">
        <Database className="icon ptree-collection-icon" aria-hidden="true" />
        <h2>{summary.collection}</h2>
        {root ? (
          <Badge tone="info" title={root.name}>
            {root.displayName ?? root.name}
          </Badge>
        ) : release.loading ? (
          <Badge title="The pinned release's schema is loading">schema loading…</Badge>
        ) : (
          <Badge title="No profile template of this name in the pinned release">untyped</Badge>
        )}
        <span className="muted small">
          {summary.keys} key{Number(summary.keys) === 1 ? '' : 's'} · {formatBytesShort(summary.bytes)}
          {summary.updatedAt ? ` · updated ${formatInstant(summary.updatedAt, zone, { offsets })}` : ''}
        </span>
        <span className="spacer" />
        {onHistory && (
          <Button size="s" variant="ghost" onClick={() => onHistory(summary.collection)}>
            History
          </Button>
        )}
      </div>
      {detail.loading && <SkeletonRows rows={2} label={`Loading ${summary.collection}`} />}
      {detail.error ? (
        <div className="ptree-error">
          <LoadError>Could not load this collection.</LoadError>
        </div>
      ) : null}
      {sections.map((p) => (
        <KeyNode key={p.name} env={env} player={player} collection={summary.collection} keyName={p.name} server={byName.get(p.name)} sectionTemplate={p.template ? release.templates.get(p.template) : undefined} release={release} canWrite={canWrite} onHistory={onHistory} />
      ))}
      {extra.map((k) => (
        <KeyNode key={k.key} env={env} player={player} collection={summary.collection} keyName={k.key} server={k} sectionTemplate={undefined} release={release} canWrite={canWrite} onHistory={onHistory} unknown={!!root && !READ_ONLY_KEYS.has(k.key)} />
      ))}
    </section>
  );
}

interface KeyNodeProps {
  env: string;
  player: Player;
  collection: string;
  keyName: string;
  server: SaveKey | undefined;
  sectionTemplate: Template | undefined;
  release: ReleaseInfo;
  canWrite: boolean;
  onHistory?: (collection: string, key?: string) => void;
  /** the root template has no section of this name */
  unknown?: boolean;
}

function KeyNode({ env, player, collection, keyName, server, sectionTemplate, release, canWrite, onHistory, unknown }: KeyNodeProps) {
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const id = playerKeyId(env, player.id, collection, keyName);
  const draft = workspace.getDraft(entityKey('player-key', id));
  const serverValue = (server?.value ?? null) as Json;
  const value: Json = draft ? draft.current : serverValue;
  const stored = !!server;
  const locked = READ_ONLY_KEYS.has(keyName);
  const mode: EditMode = { locked, missing: canWrite ? null : WRITE_PERMISSION, readOnly: locked || !canWrite };
  const [giving, setGiving] = useState(false);
  const stage = (path: string, v: Json) => {
    workspace.edit('player-key', id, path, v, { base: { version: server ? Number(server.version) : null, value: serverValue }, label: `${collection}/${keyName} · player ${shortPlayerId(player.id)}`, template: sectionTemplate?.name });
  };
  const o = obj(value);
  const actions: ReactNode[] = [];
  if (!locked && keyName === 'inventories' && sectionTemplate) {
    actions.push(
      <Button key="give" size="s" missingPermission={mode.missing} onClick={() => setGiving(true)}>
        Give items…
      </Button>,
    );
  }
  if (!locked && keyName === 'smartInfo' && o && Array.isArray(o[COOLDOWN_LIST]) && (o[COOLDOWN_LIST] as Json[]).length > 0) {
    actions.push(
      <Button key="cooldowns" size="s" missingPermission={mode.missing} onClick={() => stage(COOLDOWN_LIST, [])}>
        Clear all cooldowns
      </Button>,
    );
  }
  return (
    <details className="ptree-key" data-testid={`key-${collection}-${keyName}`}>
      <summary>
        <ChevronRight className="ptree-chevron" aria-hidden="true" />
        <span className="ptree-key-name">{keyName}</span>
        {sectionTemplate && <span className="muted small">{sectionTemplate.displayName ?? sectionTemplate.name}</span>}
        {unknown && <Badge tone="warning" title="The pinned release's profile template has no section of this name">not in the schema</Badge>}
        {locked && <Badge title="The server's bump rule owns this section">read-only</Badge>}
        {draft && <Badge tone="warning">modified</Badge>}
        {server && (
          <span className="muted small row ptree-version">
            v{String(server.version)}
            <Badge tone={SOURCE_TONE[server.source] ?? 'neutral'} title="The source of the current version">
              {server.source}
            </Badge>
            {formatBytesShort(server.bytes)}
          </span>
        )}
        {!stored && !draft && <span className="muted small">not stored</span>}
        <span className="spacer" />
        {onHistory && (
          <Button
            size="s"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              onHistory(collection, keyName);
            }}
          >
            History
          </Button>
        )}
      </summary>
      <div className="ptree-key-body" title={mode.missing && !locked ? `You lack the '${mode.missing}' permission.` : undefined}>
        {actions.length > 0 && <div className="row ptree-actions">{actions}</div>}
        {!stored && !draft ? (
          <div className="row">
            <span className="ptree-muted-note">The player never wrote this key.</span>
            {sectionTemplate && !locked && (
              <Button size="s" missingPermission={mode.missing} onClick={() => stage('', sectionDefaults(sectionTemplate, release.templates))}>
                Set defaults
              </Button>
            )}
          </div>
        ) : sectionTemplate && o ? (
          <ObjectFields template={sectionTemplate} value={value} mode={mode} onCommitAt={stage} release={release} />
        ) : (
          <JsonField param={jsonParam(keyName)} value={value ?? undefined} onCommit={(v) => stage('', v)} disabled={mode.readOnly} />
        )}
      </div>
      {giving && (
        <GiveItemsDialog
          open
          release={release}
          inventories={value}
          onClose={() => setGiving(false)}
          onStage={(path, v) => {
            stage(path, v);
            setGiving(false);
          }}
        />
      )}
    </details>
  );
}

interface FieldsProps {
  template: Template;
  value: Json;
  mode: EditMode;
  /** a path relative to the object; `''` commits the whole object */
  onCommitAt: (path: string, value: Json) => void;
  release: ReleaseInfo;
  depth?: number;
}

/** The fields of a section or a row, one line per parameter of its template; unknown keys as JSON leaves. */
export function ObjectFields({ template, value, mode, onCommitAt, release, depth = 0 }: FieldsProps) {
  const catalog = useContent();
  const o = obj(value) ?? {};
  const named = new Set(template.effectiveParameters.map((p) => p.name));
  const extras = Object.keys(o).filter((k) => !named.has(k));
  return (
    <div className="ptree-fields">
      {template.effectiveParameters.map((p) => (
        <div className="ptree-field" key={p.name} data-testid={`field-${p.name}`}>
          <span className="ptree-field-label">
            <span>{p.displayName ?? p.name}</span>
            <span className="type">{typeLabel(p, catalog.enums, catalog.templates)}</span>
          </span>
          <FieldOf param={p} value={o[p.name]} mode={mode} onCommitAt={(rel, v) => onCommitAt(joinPath(p.name, rel), v)} release={release} depth={depth} />
        </div>
      ))}
      {extras.map((k) => (
        <div className="ptree-field" key={k} data-testid={`field-${k}`}>
          <span className="ptree-field-label">
            <span>{k}</span>
            <Badge tone="warning" title="The section's template has no field of this name">
              not in the schema
            </Badge>
          </span>
          <JsonField param={jsonParam(k)} value={o[k]} onCommit={(v) => onCommitAt(k, v)} disabled={mode.readOnly} />
        </div>
      ))}
    </div>
  );
}

function FieldOf({ param: p, value, mode, onCommitAt, release, depth }: { param: Parameter; value: Json | undefined; mode: EditMode; onCommitAt: (path: string, value: Json) => void; release: ReleaseInfo; depth: number }) {
  const { offsets } = useGame();
  const commit = (v: Json) => onCommitAt('', v);
  const label = p.displayName ?? p.name;
  const kind = timeKind(p);
  if (kind === 'instant') return <DateTimeInput value={typeof value === 'number' && value > 0 ? value : null} onCommit={(s) => commit(s ?? 0)} ctx={{ offsets }} allowEmpty disabled={mode.readOnly} aria-label={label} />;
  if (kind === 'duration') {
    if (typeof value === 'number' && value < 0) {
      return (
        <span className="row">
          <span className="ptree-muted-note">none</span>
          {!mode.locked && (
            <Button size="s" variant="ghost" missingPermission={mode.missing} onClick={() => commit(0)}>
              Set
            </Button>
          )}
        </span>
      );
    }
    return <DurationInput value={typeof value === 'number' ? value : 0} onCommit={(s) => commit(s)} zeroMeans="none" disabled={mode.readOnly} aria-label={label} />;
  }
  switch (p.type) {
    case 'profile_data': {
      const t = p.template ? release.templates.get(p.template) : undefined;
      if (!obj(value)) {
        return (
          <span className="row">
            <span className="ptree-muted-note">not set</span>
            {t && !mode.locked && (
              <Button size="s" variant="ghost" missingPermission={mode.missing} onClick={() => commit(sectionDefaults(t, release.templates))}>
                Set defaults
              </Button>
            )}
          </span>
        );
      }
      if (!t || depth > 6) return <JsonField param={jsonParam(p.name)} value={value} onCommit={commit} disabled={mode.readOnly} />;
      return (
        <div className="ptree-nested">
          <ObjectFields template={t} value={value as Json} mode={mode} onCommitAt={onCommitAt} release={release} depth={depth + 1} />
        </div>
      );
    }
    case 'document':
      return <RefLabel id={typeof value === 'string' ? value : null} />;
    case 'list': {
      if (p.of?.type === 'profile_data') return <RowsNode param={p} rows={Array.isArray(value) ? value : []} mode={mode} onCommitAt={onCommitAt} release={release} depth={depth} />;
      if (p.of?.type === 'document') {
        const ids = Array.isArray(value) ? value : [];
        return (
          <span className="row wrap">
            {ids.map((id, i) => (
              <RefLabel key={i} id={typeof id === 'string' ? id : null} />
            ))}
            {ids.length === 0 && <span className="ptree-muted-note">none</span>}
          </span>
        );
      }
      break;
    }
    case 'asset':
    case 'product':
    case 'localized_string':
    case 'object':
    case 'any':
    case 'template_ref':
    case 'profile_path':
      return <JsonField param={{ ...p, type: 'any' } as Parameter} value={value} onCommit={commit} disabled={mode.readOnly} />;
    default:
      break;
  }
  return <FieldWidget param={p} value={value} onCommit={commit} onCommitAt={onCommitAt} disabled={mode.readOnly} />;
}

/** The rows of a profile list: named, expandable, removable; "Add" appends the element template's defaults. */
function RowsNode({ param: p, rows, mode, onCommitAt, release, depth }: { param: Parameter; rows: Json[]; mode: EditMode; onCommitAt: (path: string, value: Json) => void; release: ReleaseInfo; depth: number }) {
  const catalog = useContent();
  const [zone] = useTimeZone();
  const { offsets } = useGame();
  const t = p.of?.template ? release.templates.get(p.of.template) : undefined;
  const short = t?.name.split('.').pop();
  const removeLabel = INSTANCE_LISTS.has(p.name) ? 'Remove this instance' : p.name === COOLDOWN_LIST ? 'Clear this cooldown' : 'Remove';
  const titleCtx = { format: catalog.format, zone, offsets };
  const add = () => onCommitAt('', [...rows, short === 'Purchase' ? purchaseDefaults(t, release.templates, Math.floor(Date.now() / 1000)) : sectionDefaults(t, release.templates)]);
  return (
    <div className="ptree-rows" data-testid={`rows-${p.name}`}>
      {rows.length === 0 && <span className="ptree-muted-note">none</span>}
      {rows.map((row, i) => (
        <details className="ptree-row" key={i} data-testid={`row-${p.name}-${i}`}>
          <summary>
            <ChevronRight className="ptree-chevron" aria-hidden="true" />
            <span className="ptree-row-title">{rowTitle(t, row, i, titleCtx)}</span>
            {short === 'InventorySlot' && !mode.locked && <SlotAmount row={row} disabled={mode.readOnly} title={mode.missing ? `You lack the '${mode.missing}' permission.` : undefined} onCommit={(a) => onCommitAt(`[${i}].item.a`, a)} />}
            {!mode.locked && (
              <Button
                size="s"
                variant="ghost"
                aria-label={`${removeLabel} ${i + 1}`}
                missingPermission={mode.missing}
                onClick={(e) => {
                  e.preventDefault();
                  onCommitAt('', withoutIndex(rows, i));
                }}
              >
                {removeLabel}
              </Button>
            )}
          </summary>
          <div className="ptree-row-body">
            {t && obj(row) ? <ObjectFields template={t} value={row} mode={mode} onCommitAt={(rel, v) => onCommitAt(joinPath(`[${i}]`, rel), v)} release={release} depth={depth + 1} /> : <JsonField param={jsonParam(`${p.name}[${i}]`)} value={row} onCommit={(v) => onCommitAt(`[${i}]`, v)} disabled={mode.readOnly} />}
          </div>
        </details>
      ))}
      {!mode.locked && t && (
        <div>
          <Button size="s" missingPermission={mode.missing} onClick={add}>
            {short === 'Purchase' ? 'Add purchase' : `Add ${t.displayName ?? short}`}
          </Button>
        </div>
      )}
    </div>
  );
}

function SlotAmount({ row, disabled, title, onCommit }: { row: Json; disabled?: boolean; title?: string; onCommit: (amount: number) => void }) {
  const a = obj(obj(row)?.item)?.a;
  return (
    <span className="row" onClick={(e) => e.preventDefault()} title={title}>
      <NumberInput size="s" kind="int" value={typeof a === 'number' ? a : 0} onCommit={(v) => onCommit(Number(v))} aria-label="Set amount" className="ptree-amount" disabled={disabled} />
    </span>
  );
}

/** A document reference, read-only: the label through the release's branch with a link to its editor; the id when unknown. */
export function RefLabel({ id }: { id: string | null }) {
  const catalog = useContent();
  const { org, game } = useGame();
  if (!id) return <span className="ptree-muted-note">—</span>;
  const doc = catalog.resolve(id);
  if (!doc) {
    return (
      <span className="ptree-ref" title={id} data-testid={`ref-${id}`}>
        <code className="small">{id}</code>
        {catalog.branch && catalog.resolver.known(id) && <span className="muted small">not found</span>}
      </span>
    );
  }
  const label = catalog.label(doc);
  const href = catalog.branch ? `${routes.documents(org, game.slug, catalog.branch, doc.template)}?doc=${encodeURIComponent(doc.id)}` : null;
  return (
    <span className="ptree-ref" data-testid={`ref-${id}`}>
      {href ? <Link to={href}>{label}</Link> : <span>{label}</span>}
      <span className="muted small">{doc.template.split('.').pop()}</span>
    </span>
  );
}
